import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clubApi } from "../../club-api-client";
import type { CreateTrainingSlot, UpdateTrainingSlot } from "../../types";
import { queryKeys } from "../query-keys";

export function useTrainingSlots(clubId: string | null, groupId: string | null) {
	return useQuery({
		queryKey: queryKeys.clubPortal.trainingSlots(clubId ?? "", groupId ?? ""),
		queryFn: () => clubApi.training.slots.list(clubId!, groupId!),
		enabled: !!clubId && !!groupId,
	});
}

export function useCreateTrainingSlot(clubId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ groupId, data }: { groupId: string; data: CreateTrainingSlot }) =>
			clubApi.training.slots.create(clubId, groupId, data),
		onSuccess: () =>
			qc.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "training"],
			}),
	});
}

export function useUpdateTrainingSlot(clubId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({
			groupId,
			slotId,
			data,
		}: {
			groupId: string;
			slotId: string;
			data: UpdateTrainingSlot;
		}) => clubApi.training.slots.update(clubId, groupId, slotId, data),
		onSuccess: () =>
			qc.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "training"],
			}),
	});
}

export function useDeleteTrainingSlot(clubId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ groupId, slotId }: { groupId: string; slotId: string }) =>
			clubApi.training.slots.delete(clubId, groupId, slotId),
		onSuccess: () =>
			qc.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "training"],
			}),
	});
}
